import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import dotProp from 'dot-prop-immutable';
import { v4 as uuid } from 'uuid';
import {Link} from '@reach/router';
import { motion, AnimatePresence } from 'framer-motion';
import axiosUI from '../../axiosUI';
import Sidebar from '../Sidebar/Sidebar';
import Topbar from '../Topbar/Topbar';
import Loader from '../Loader/Loader';
import Main from '../Main/Main';
import Button from '../Button/Button';
import PopupWrapper from '../PopupWrapper';
import { withGlobalContext } from '../../App';
import BannerCard from './BannerCard';
import {
  NOTIFICATIONS,
} from '../../constants';
import withCommonUtils from "../withCommonUtils";
import MarketSwitcher from "../MarketSwitcher";

class BannersPerMarket extends Component {
  static propTypes = {
    country: PropTypes.string,
    language: PropTypes.string,
    createNotification: PropTypes.func,
    navigate: PropTypes.func,
  };

  constructor(props) {
    super(props);

    this.state = {
      bannerConfigList: [],
      showUnsavedChangesWarningPopup: false,
      showDeleteWarningPopup: false,
      loading: false,
    };
  }

  componentDidMount = async () => {
    await this.reloadBanners();
  };

  componentDidUpdate = async (prevProps) => {
    if (this.props.country !== prevProps.country || this.props.language !== prevProps.language) {
      await this.reloadBanners();
    }
  };


  reloadBanners = async () => {
    this.setState({ loading: true }, async () => {
      try {
        let { data } = await axiosUI.get(
          `/api/banners?salesChannel=${this.props.country}&locale=${this.props.language}`
        );
        const hasDrafts = data.some(banner => banner.state === 'DRAFT');
        if (hasDrafts) {
          const bannerDrafts = data.reduce((drafts, banner) => {
            if (banner.state === 'DRAFT') {
              drafts[banner.id] = true;
            }
            return drafts;
          }, {});

          // remove entries which are both in published and draft stage
          data = data.filter(banner => {
            const { state, id } = banner;
            return !(state === 'PUBLISHED' && bannerDrafts[id]);
          });
        }

        this.setState({ bannerConfigList: data, hasDrafts });
      } catch (err) {
        console.log(err);
        this.props.createNotification({
          message: `Failed to fetch banners`,
          type: NOTIFICATIONS.TYPE.ERROR,
          timeout: 3000,
        });
      }

      this.setState({ loading: false });
    });
  }

  toggleUnsavedChangesWarningPopup = () => {
    this.setState(prevState => ({
      showUnsavedChangesWarningPopup: !prevState.showUnsavedChangesWarningPopup,
    }));
  };

  toggleDeleteWarningPopup = configIndex => {
    this.setState(prevState => {
      return {
        showDeleteWarningPopup:
          prevState.showDeleteWarningPopup === false ? configIndex : false,
      };
    });
  };

  createNewConfig = () => {
    const newConfigDataModel = {
      id: uuid(),
      title: '',
      description: '',
      icon: '',
      contexts: [],
      state: 'UNSAVED',
      operation_type: 'CREATE',
      sales_channel_id: this.props.country,
      locale: this.props.language,
      online: true,
    };

    this.setState(prevState => {
      return {
        bannerConfigList: [newConfigDataModel, ...prevState.bannerConfigList],
      };
    });
  };

  addNewContext = configIndex => {
    this.setState(prevState => {
      const currentBannerConfig = prevState.bannerConfigList[configIndex];
      const newContext = { key: '', value: '', id: uuid() };

      const updatedBannerConfigAtIndex = {
        ...currentBannerConfig,
        contexts: [newContext, ...currentBannerConfig.contexts],
        operation_type: this.getNewOperationType(currentBannerConfig),
        state: 'UNSAVED',
      };
      return {
        bannerConfigList: dotProp.set(
          prevState.bannerConfigList,
          configIndex,
          updatedBannerConfigAtIndex
        ),
      };
    });
  };

  removeContext = (configIndex, contextIndex) => {
    this.setState(prevState => {
      const currentBannerConfig = prevState.bannerConfigList[configIndex];
      const currentContexts = currentBannerConfig.contexts;
      const updatedBannerConfigAtIndex = {
        ...currentBannerConfig,
        contexts: [
          ...currentContexts.slice(0, contextIndex),
          ...currentContexts.slice(contextIndex + 1),
        ],
        state: 'UNSAVED',
        operation_type: this.getNewOperationType(currentBannerConfig),
      };
      return {
        bannerConfigList: dotProp.set(
          prevState.bannerConfigList,
          configIndex,
          updatedBannerConfigAtIndex
        ),
      };
    });
  };

  removeConfig = async configIndex => {
    // if draft -> warn user before discarding and then make an api call
    // if unsaved -> just remove it
    // if published -> set operation_type to 'DELETE' and block editing on it

    let currentBanner = this.state.bannerConfigList[configIndex];
    switch (currentBanner.state) {
      case 'DRAFT': {
        try {
          const currentBanner = this.state.bannerConfigList[configIndex];
          const url = '/api/banners/drafts';
          await axiosUI.delete(url, { data: [currentBanner] });

          this.props.createNotification({
            message: `Draft is discarded`,
            type: NOTIFICATIONS.TYPE.INFO,
            timeout: 5000,
          });

          this.setState(prevState => {
            return {
              bannerConfigList: [
                ...prevState.bannerConfigList.slice(0, configIndex),
                ...prevState.bannerConfigList.slice(configIndex + 1),
              ],
              showDeleteWarningPopup: false,
            };
          });
        } catch (err) {
          console.log(err);
        }
        break;
      }
      case 'UNSAVED': {
        this.setState(prevState => {
          return {
            bannerConfigList: [
              ...prevState.bannerConfigList.slice(0, configIndex),
              ...prevState.bannerConfigList.slice(configIndex + 1),
            ],
          };
        });
        break;
      }
      case 'PUBLISHED': {
        currentBanner = {
          ...currentBanner,
          operation_type: 'DELETE',
          state: 'UNSAVED',
        };
        this.setState({
          bannerConfigList: dotProp.set(
            this.state.bannerConfigList,
            configIndex,
            currentBanner
          ),
        });
        break;
      }
      default:
        break;
    }
  };

  submitChange = async () => {
    const unsavedChanges = this.state.bannerConfigList.filter(
      banner => banner.state === 'UNSAVED'
    );

    unsavedChanges.forEach(banner => {
      banner.contexts = banner.contexts.map(context => ({
        ...context,
        key: context.key.trim(),
        value: context.value.trim(),
      }));
    });

    const areThereAnyRequiredFieldsEmpty = unsavedChanges.some(
      banner => !banner.description.trim()
    );

    if (areThereAnyRequiredFieldsEmpty) {
      return this.props.createNotification({
        message: `Description field cannot be empty`,
        type: NOTIFICATIONS.TYPE.ERROR,
        timeout: 3000,
      });
    }

    this.setState({ loading: true }, async () => {
      try {
        const url = '/api/banners/';
        await axiosUI.post(url, unsavedChanges);

        this.props.createNotification({
          message: `Your changes are submitted for review`,
          type: NOTIFICATIONS.TYPE.SUCCESS,
          timeout: 5000,
        });

        setTimeout(() => {
          this.navigateToChangesetPage();
        }, 3000);
      } catch (err) {
        this.props.createNotification({
          message: `Failed to save the changes`,
          type: NOTIFICATIONS.TYPE.ERROR,
          timeout: 3000,
        });
      }

      this.setState({ loading: false });
    });
  };

  onChange = (event, configIndex) => {
    const {
      target: { name, checked, value, type },
    } = event;
    const currentBannerConfig = this.state.bannerConfigList[configIndex];
    const fieldValue = type === 'checkbox' ? checked : value;
    const updatedBannerConfigList = dotProp.set(
      this.state.bannerConfigList,
      name,
      fieldValue
    );
    updatedBannerConfigList[
      configIndex
    ].operation_type = this.getNewOperationType(currentBannerConfig);
    updatedBannerConfigList[configIndex].state = 'UNSAVED';
    this.setState({ bannerConfigList: updatedBannerConfigList });
  };

  handleIconUpload = (event, configIndex) => {
    const currentBannerConfig = this.state.bannerConfigList[configIndex];
    const reader = new FileReader();
    reader.readAsDataURL(event.target.files[0]);
    reader.addEventListener('load', () => {
      this.setState({
        bannerConfigList: dotProp.set(
          this.state.bannerConfigList,
          configIndex,
          {
            ...this.state.bannerConfigList[configIndex],
            icon: reader.result,
            state: 'UNSAVED',
            operation_type: this.getNewOperationType(currentBannerConfig),
          }
        ),
      });
    });
  };

  removeIcon = configIndex => {
    const currentBannerConfig = this.state.bannerConfigList[configIndex];
    this.setState({
      bannerConfigList: dotProp.set(this.state.bannerConfigList, configIndex, {
        ...this.state.bannerConfigList[configIndex],
        icon: '',
        state: 'UNSAVED',
        operation_type: this.getNewOperationType(currentBannerConfig),
      }),
    });
  };

  getNewOperationType = config => {
    const oldState = config.state;
    const oldOperationType = config.operation_type;
    if (
      oldState === 'PUBLISHED' ||
      (oldState === 'DRAFT' && oldOperationType === 'UPDATE')
    ) {
      return 'UPDATE';
    }
    return 'CREATE';
  };

  navigateToChangesetPage = () => {
    this.props.navigate('changeset');
  };

  render() {
    const hasUnsavedChanges = this.state.bannerConfigList.some(
      banner => banner.state === 'UNSAVED'
    );

    return (
      <Fragment>
        <Sidebar highlightNavItemIndex={2} />
        <Topbar className="market_banners__topbar flex justify-between items-center">
          <span className="font-bold text-lg capitalize">
            <Link className="bb-1-blue no-underline text-blue mr-2" to="/banners">
              Banners
            </Link>
            /
            <MarketSwitcher {...this.props} basePath='/banners'/>
          </span>
          <span className="float-right text-right">
            <Button className="h-8" onClick={this.createNewConfig}>
              Add new Banner
            </Button>
            {this.state.hasDrafts && (
              <PopupWrapper
                className="relative"
                isOpen={this.state.showUnsavedChangesWarningPopup}
                togglePopup={this.toggleUnsavedChangesWarningPopup}>
                <Button
                  type="blue"
                  className="h-8 ml-8"
                  onClick={
                    hasUnsavedChanges
                      ? this.toggleUnsavedChangesWarningPopup
                      : this.navigateToChangesetPage
                  }>
                  Review Pending Draft
                </Button>
                <AnimatePresence>
                  {this.state.showUnsavedChangesWarningPopup && (
                    <motion.div
                      initial={{ opacity: 0, y: '80%' }}
                      animate={{ opacity: 1, y: '100%' }}
                      exit={{ opacity: 0, y: '80%' }}
                      transition={{
                        ease: [0.18, 0.89, 0.39, 1.4],
                        duration: 0.3,
                      }}
                      className="button-popup absolute pin-r px-2 py-4 bg-white shadow-md rounded text-left w-64">
                      <div className="text-center leading-normal">
                        There are some unsaved changes. Are you sure you want to
                        leave this page?
                      </div>
                      <div className="flex justify-around mt-8">
                        <Button
                          className="h-8"
                          onClick={this.toggleUnsavedChangesWarningPopup}>
                          No
                        </Button>
                        <Button
                          type="red-transparent"
                          className="h-8"
                          onClick={this.navigateToChangesetPage}>
                          Yes, leave this page
                        </Button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </PopupWrapper>
            )}
            <Button
              type="green"
              className="h-8 ml-8"
              onClick={this.submitChange}>
              Save as Draft
            </Button>
          </span>
        </Topbar>
        <Main className="market_banners__main overflow-x-hidden">
          <Loader loading={this.state.loading}>
            <ul className="list-reset m-0">
              <AnimatePresence initial={false}>
                {this.state.bannerConfigList.map((config, configIndex) => {
                  const { state, operation_type } = config;
                  const isDeleted = operation_type === 'DELETE';
                  return (
                    <BannerCard
                      key={config.id}
                      addNewContext={this.addNewContext}
                      bannerConfigList={this.state.bannerConfigList}
                      bannerState={state}
                      config={config}
                      configIndex={configIndex}
                      handleIconUpload={this.handleIconUpload}
                      isDeleted={isDeleted}
                      onChange={this.onChange}
                      removeConfig={this.removeConfig}
                      removeContext={this.removeContext}
                      removeIcon={this.removeIcon}
                      showDeleteWarningPopup={this.state.showDeleteWarningPopup}
                      toggleDeleteWarningPopup={this.toggleDeleteWarningPopup}
                    />
                  );
                })}
              </AnimatePresence>
            </ul>
          </Loader>
        </Main>
      </Fragment>
    );
  }
}

export default  withCommonUtils(withGlobalContext(BannersPerMarket));
